import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import {
  assetDetail, audioURL, transcriptURL, rerenderAsset, republishAsset, unpublishAsset, patchAsset, getJob,
  type AssetDetailResponse, type JobRow,
} from "../lib/api";

export default function AssetDetail() {
  const { assetId = "" } = useParams();
  const [data, setData]   = useState<AssetDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null); setData(null); setTranscript(null);
    assetDetail(assetId)
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    // Transcript is text/plain; use the ?t= fallback so we don't have to
    // attach a custom header (consistent with the <audio> tag below, and
    // means a future "Open transcript in new tab" button works too).
    fetch(`${transcriptURL(assetId)}?t=${encodeURIComponent(localStorage.getItem("rainfall.admin.bearer") ?? "")}`)
      .then(r => (r.ok ? r.text() : Promise.resolve(null)))
      .then(t => { if (!cancelled && t) setTranscript(t); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [assetId]);

  function refresh() {
    assetDetail(assetId).then(setData).catch(e => setError(e instanceof Error ? e.message : String(e)));
  }

  if (error) return <div className="text-sm text-rainfall-bad">{error}</div>;
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  const a = data.asset;
  const p = data.plays;
  const completionPct = p.total > 0 ? Math.round((p.completed / p.total) * 100) : null;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/library" className="text-xs text-slate-500 hover:text-slate-300">← Library</Link>
        <div className="flex items-baseline gap-4 mt-2">
          <h1 className="text-xl font-semibold">{a.theme} · {a.voice}</h1>
          <span className={`text-xs px-2 py-0.5 rounded border ${a.tier === "paid"
            ? "bg-yellow-500/15 text-yellow-200 border-yellow-500/30"
            : "bg-slate-700/30 text-slate-300 border-slate-600/40"}`}>{a.tier}</span>
          <code className="text-xs text-slate-500">{a.asset_id}</code>
        </div>
        {a.summary && <p className="italic text-slate-400 mt-2">{a.summary}</p>}
      </div>

      <ActionRow assetId={a.asset_id} published={!!a.published_at} onChanged={refresh} />

      <Card title="Audio">
        <audio
          controls
          preload="metadata"
          src={`${audioURL(a.asset_id)}?t=${encodeURIComponent(localStorage.getItem("rainfall.admin.bearer") ?? "")}`}
          className="w-full"
        />
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Total plays"    value={p.total} />
        <Stat label="Completion"     value={completionPct == null ? "—" : `${completionPct}%`} />
        <Stat label="Plays today"    value={p.d1} />
        <Stat label="Plays 7d"       value={p.d7} />
        <Stat label="Plays 30d"      value={p.d30} />
        <Stat label="Paid plays"     value={p.paid} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card title="Parameters">
          <Kv k="Length"           v={`${a.length} min`} />
          <Kv k="Bed"              v={a.bed} />
          <Kv k="Time of day"      v={a.time_of_day} />
          <Kv k="Script version"   v={`v${a.script_version} · variant ${a.variant}`} />
          <Kv k="Voice config"     v={a.voice_config_id} />
          <Kv k="Bed asset"        v={a.bed_id} />
          <Kv k="Duration"         v={`${a.duration_sec}s`} />
          <Kv k="Source"           v={a.source} />
          <Kv k="Created"          v={a.created_at} />
          <Kv k="Published"        v={a.published_at ?? "—"} />
        </Card>

        <Card title="Quality">
          <Kv k="Passed" v={a.quality_passed == null ? "—" : a.quality_passed === 1 ? "yes" : "no"} />
          {a.quality_flags && <Kv k="Flags"   v={a.quality_flags} />}
          {a.quality_metrics && (
            <pre className="text-xs text-slate-400 mt-2 max-h-48 overflow-auto bg-slate-950/60 p-2 rounded">
              {pretty(a.quality_metrics)}
            </pre>
          )}
        </Card>
      </div>

      {transcript && (
        <Card title="Transcript">
          <pre className="text-sm leading-relaxed text-slate-300 whitespace-pre-wrap font-sans">
            {transcript}
          </pre>
        </Card>
      )}

      <Card title="Generation history">
        {data.jobs.length === 0 ? (
          <div className="text-sm text-slate-500">No jobs recorded.</div>
        ) : (
          <table className="min-w-full text-xs">
            <thead className="text-slate-500"><tr>
              <th className="text-left font-normal py-1">Job</th>
              <th className="text-left font-normal py-1">Status</th>
              <th className="text-left font-normal py-1">Requested</th>
              <th className="text-left font-normal py-1">Finished</th>
              <th className="text-left font-normal py-1">Error</th>
            </tr></thead>
            <tbody>
              {data.jobs.map(j => (
                <tr key={j.job_id} className="border-t border-slate-900">
                  <td className="py-1 font-mono">{j.job_id.slice(0, 8)}…</td>
                  <td className="py-1"><JobStatus s={j.status} /></td>
                  <td className="py-1">{j.created_at}</td>
                  <td className="py-1">{j.finished_at ?? "—"}</td>
                  <td className="py-1 text-rainfall-bad">{j.error ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title={`Recent plays (${data.recentPlays.length})`}>
        {data.recentPlays.length === 0 ? (
          <div className="text-sm text-slate-500">No plays yet.</div>
        ) : (
          <table className="min-w-full text-xs">
            <thead className="text-slate-500"><tr>
              <th className="text-left font-normal py-1">Started</th>
              <th className="text-left font-normal py-1">Install</th>
              <th className="text-left font-normal py-1">Tier</th>
              <th className="text-left font-normal py-1">Device</th>
              <th className="text-left font-normal py-1">Progress</th>
              <th className="text-left font-normal py-1">Done</th>
              <th className="text-left font-normal py-1">Reason</th>
            </tr></thead>
            <tbody>
              {data.recentPlays.map(pl => (
                <tr key={pl.play_id} className="border-t border-slate-900">
                  <td className="py-1">{pl.started_at}</td>
                  <td className="py-1 font-mono">{pl.install_id.slice(0, 8)}…</td>
                  <td className="py-1">{pl.tier_at_play}</td>
                  <td className="py-1">{pl.device_kind}</td>
                  <td className="py-1">{pl.progress_sec}/{pl.duration_sec_at_play}s</td>
                  <td className="py-1">{pl.completed ? "✓" : "—"}</td>
                  <td className="py-1">{pl.end_reason ?? "in-flight"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function ActionRow({ assetId, published, onChanged }: { assetId: string; published: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg]   = useState<string | null>(null);
  const [pollingJob, setPollingJob] = useState<{ jobId: string; status: string } | null>(null);

  async function doAction(label: string, fn: () => Promise<unknown>) {
    setBusy(label); setMsg(null);
    try {
      const r = await fn();
      setMsg(`${label} OK${typeof r === "object" && r && "version" in r ? ` (catalog v${(r as { version: number }).version})` : ""}`);
      onChanged();
    } catch (e) {
      setMsg(`${label} failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  async function doRerender() {
    setBusy("rerender"); setMsg(null); setPollingJob(null);
    try {
      const r = await rerenderAsset(assetId);
      setMsg(`Re-render enqueued — new assetId ${r.assetId.slice(0, 8)}… job ${r.jobId.slice(0, 8)}…`);
      setPollingJob({ jobId: r.jobId, status: "queued" });
      pollJobUntilTerminal(r.jobId, status => setPollingJob({ jobId: r.jobId, status }))
        .then(final => setPollingJob({ jobId: r.jobId, status: final }))
        .catch(() => {});
    } catch (e) {
      setMsg(`Re-render failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card title="Actions">
      <div className="flex flex-wrap items-center gap-2">
        <ActionBtn onClick={doRerender} disabled={busy !== null} label="Re-render (variant +1)" busyLabel="Re-render…" busy={busy === "rerender"} />
        {published
          ? <ActionBtn onClick={() => doAction("Unpublish", () => unpublishAsset(assetId))} disabled={busy !== null} label="Unpublish" busy={busy === "Unpublish"} />
          : <ActionBtn onClick={() => doAction("Republish", () => republishAsset(assetId))} disabled={busy !== null} label="Publish" busy={busy === "Republish"} />}
        <SummaryEditor assetId={assetId} onSaved={onChanged} />
      </div>
      {pollingJob && (
        <div className="mt-3 text-xs text-slate-400 font-mono">
          Job <span className="text-rainfall-accent">{pollingJob.jobId.slice(0, 12)}…</span> →
          <span className={pollingJob.status === "done" ? "text-emerald-300 ml-1" : pollingJob.status === "failed" ? "text-rose-300 ml-1" : "text-yellow-200 ml-1"}>
            {pollingJob.status}
          </span>
        </div>
      )}
      {msg && <div className="mt-2 text-xs text-slate-400">{msg}</div>}
    </Card>
  );
}

function ActionBtn({ onClick, label, disabled, busy, busyLabel }: { onClick: () => void; label: string; disabled: boolean; busy: boolean; busyLabel?: string }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="text-sm px-3 py-1.5 rounded border border-slate-700 hover:border-rainfall-accent disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {busy ? (busyLabel ?? `${label}…`) : label}
    </button>
  );
}

function SummaryEditor({ assetId, onSaved }: { assetId: string; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try { await patchAsset(assetId, { summary: text || null }); setEditing(false); onSaved(); }
    catch { /* swallow — UI sticks open with whatever the user typed */ }
    finally { setBusy(false); }
  }
  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} className="text-sm px-3 py-1.5 rounded border border-slate-700 hover:border-rainfall-accent">
        Edit summary
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 w-full">
      <input
        autoFocus
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="New summary (≤ 240 chars)"
        className="flex-1 bg-slate-900 border border-slate-800 rounded px-3 py-1.5 text-sm outline-none focus:border-rainfall-accent"
        maxLength={240}
      />
      <button onClick={save} disabled={busy} className="text-sm px-3 py-1.5 rounded bg-rainfall-accent text-slate-950 disabled:opacity-50">
        {busy ? "Saving…" : "Save"}
      </button>
      <button onClick={() => setEditing(false)} className="text-sm px-3 py-1.5 rounded border border-slate-700">
        Cancel
      </button>
    </div>
  );
}

async function pollJobUntilTerminal(jobId: string, onTick: (status: string) => void): Promise<string> {
  for (let i = 0; i < 240; i++) {
    let j: JobRow;
    try { j = await getJob(jobId); }
    catch { await sleep(2000); continue; }
    onTick(j.status);
    if (j.status === "done" || j.status === "failed" || j.status === "needs_review") return j.status;
    await sleep(2000);
  }
  return "timeout";
}
function sleep(ms: number) { return new Promise<void>(r => setTimeout(r, ms)); }

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border border-slate-800 rounded-lg bg-slate-900/30">
      <header className="px-4 py-2 border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">{title}</header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Kv({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between text-sm py-1 border-b border-slate-900 last:border-0">
      <span className="text-slate-500">{k}</span>
      <span className="font-mono text-slate-200">{v}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="border border-slate-800 rounded-lg bg-slate-900/30 p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-2xl font-mono mt-1">{value}</div>
    </div>
  );
}

function JobStatus({ s }: { s: string }) {
  const cls = s === "done" ? "text-emerald-300"
            : s === "failed" || s === "needs_review" ? "text-rose-300"
            : s === "running" ? "text-yellow-200"
            : "text-slate-300";
  return <span className={cls}>{s}</span>;
}

function pretty(s: string): string {
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
}
